import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Fab,
  Input,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useSetAtom } from "jotai";
import { runYearAtom } from "src/atoms/overlayers";
import { CaseOverviewTable } from "src/components/CaseOverviewTable";
import { PageHeader } from "src/components/page/PageHeader";
import { IsVisible } from "src/components/permissions";
import { usePermissions } from "src/hooks";
import { useCases } from "src/hooks/useCases";
import { CaseShareFiltersBar } from "src/pages/CaseShareFiltersBar";
import { type CASE_SHARE_FILTER, CaseCopyStatus, CaseStatus } from "src/types";

type Props = {};

export function CasesPage(props: Props) {
  const { t } = useTranslation();

  const [shareFilter, setShareFilter] = useState<CASE_SHARE_FILTER>("ALL");

  const [refetchInterval, setRefetchInterval] = useState<false | number>(false);
  const [searchQuery, setSearchQuery] = useState("");
  const setRunYear = useSetAtom(runYearAtom);
  const { data: cases } = useCases(refetchInterval);
  const { data: permissions } = usePermissions();

  /**
   * Effects
   */
  useEffect(updateRefreshInterval, [cases]);

  /**
   * Functions
   */
  function updateRefreshInterval() {
    const result = cases?.find((acase) => {
      if (acase.status === CaseStatus.PREPARING) return true;
      if (acase.status === CaseStatus.CALCULATING) return true;
      if (acase.status === CaseStatus.CALCULATION_CANCELLING) return true;
      if (acase.copyStatus === CaseCopyStatus.COPYING) return true;
      return false;
    });

    // if any case is in pending status, increase refetch interval
    if (result) return setRefetchInterval(1000);
    return setRefetchInterval(false);
  }

  function handleSearch(
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) {
    setSearchQuery(event.target.value);
  }

  return render();

  function render() {
    if (!cases) return null;

    return (
      <StyledContainer maxWidth="lg">
        {renderHeader()}
        <CaseShareFiltersBar value={shareFilter} onChange={setShareFilter} />
        <CaseOverviewTable
          className="cases-table"
          data={cases}
          searchQuery={searchQuery}
          caseShareFilter={shareFilter}
        />
      </StyledContainer>
    );
  }

  function renderHeader() {
    if (!permissions) return null;
    return (
      <PageHeader sx={{ gap: 2 }}>
        <Typography variant="h3" noWrap className="page-title">
          {t("title.cases")}
        </Typography>

        <Box display="flex" flexGrow={1} />

        {/* Purposely an uncontrolled component, no reflow for searchQuery necessary. Uncontrolled is thus faster */}
        <Input
          placeholder={t("label.searchByCaseName")}
          type="search"
          autoFocus
          onChange={handleSearch}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
        />

        <IsVisible
          permission={[
            permissions.cases.createNormal,
            permissions.cases.createPredefined,
          ]}
          type="some"
        >
          <Tooltip title={t<string>("tooltip.newCase")} placement="left">
            <Fab
              component={Link}
              to="/cases/new"
              className="button fab"
              color="primary"
              aria-label="add"
              onClick={() => setRunYear(undefined)}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        </IsVisible>
      </PageHeader>
    );
  }
}

const StyledContainer = styled(Container)`
  padding: ${({ theme }) => theme.spacing(3)};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: auto;

  input:placeholder-shown {
    text-overflow: ellipsis;
  }

  .page-title {
    margin-bottom: ${({ theme }) => theme.spacing(3)};
  }

  .cases-table {
    height: 0;
    flex: 1 1 100px;
  }

  .button {
    & + .button {
      margin-left: ${({ theme }) => theme.spacing(1)};
    }
  }
`;
